<script setup>
import { computed, ref, watch, onMounted } from 'vue';

const mainLogoError = ref(false);

const props = defineProps({
  card1Start: { type: [Number, String], default: 0 },
  card1: { type: [Number, String], default: 363416.28 },
  card1Middle: { type: [Number, String], default: 180000 },
  card2Start: { type: [Number, String], default: 0 },
  card2: { type: [Number, String], default: 91696.62 },
  card2Middle: { type: [Number, String], default: 45000 },
  card3Start: { type: [Number, String], default: 0 },
  card3: { type: [Number, String], default: 11245.19 },
  card3Middle: { type: [Number, String], default: 5600 },
  card4Start: { type: [Number, String], default: 0 },
  card4: { type: [Number, String], default: 542.28 },
  card4Middle: { type: [Number, String], default: 270 },
  secondsPerUnit: { type: Number, default: 0.5 },
  currencyCode: { type: String, default: 'TRY' },
});

const displayValues = ref([0, 0, 0, 0]);
const pulsingCards = ref([false, false, false, false]);

const formatValue = (val) => {
  const n = Number(val);
  if (isNaN(n)) return '0.00';
  const [int, dec] = n.toFixed(2).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + dec;
};

const linear = (t) => t;

const STORAGE_KEY_START = 'jackpotBellsAnimationStart';
const STORAGE_KEY_CONFIG = 'jackpotBellsCardConfig';

const getCardDuration = (start, end) => {
  const range = Math.abs(Number(end) - Number(start));
  const secPerUnit = props.secondsPerUnit ?? 0.5;
  return Math.max(500, (range / 0.01) * secPerUnit * 1000);
};

const cardProps = computed(() => [
  { start: props.card1Start, end: props.card1, middle: props.card1Middle },
  { start: props.card2Start, end: props.card2, middle: props.card2Middle },
  { start: props.card3Start, end: props.card3, middle: props.card3Middle },
  { start: props.card4Start, end: props.card4, middle: props.card4Middle },
]);

const saveAnimationState = () => {
  try {
    localStorage.setItem(STORAGE_KEY_START, String(Date.now()));
    localStorage.setItem(
      STORAGE_KEY_CONFIG,
      JSON.stringify({
        ends: [props.card1, props.card2, props.card3, props.card4],
        starts: [props.card1Start, props.card2Start, props.card3Start, props.card4Start],
        secondsPerUnit: props.secondsPerUnit,
      })
    );
  } catch (_) {}
};

const loadAndResume = () => {
  try {
    const startStr = localStorage.getItem(STORAGE_KEY_START);
    const configStr = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (!startStr || !configStr) return false;

    const startTime = Number(startStr);
    const config = JSON.parse(configStr);
    const currentEnds = [props.card1, props.card2, props.card3, props.card4].map(Number);
    const savedEnds = (config.ends || []).map(Number);
    const sameConfig =
      config.secondsPerUnit === props.secondsPerUnit &&
      savedEnds.length === 4 &&
      currentEnds.every((v, i) => Math.abs(v - (savedEnds[i] ?? 0)) < 0.01);

    if (!sameConfig) return false;

    const elapsed = Date.now() - startTime;
    const cards = cardProps.value;

    for (let i = 0; i < 4; i++) {
      const cp = cards[i];
      const duration = getCardDuration(cp.start, cp.end);
      const cycleProgress = (elapsed % duration) / duration;
      const eased = linear(cycleProgress);
      const startNum = Number(cp.start) || 0;
      const endNum = Number(cp.end) || 0;
      const middleNum = cp.middle != null && cp.middle !== '' ? Number(cp.middle) : null;
      const current = startNum + (endNum - startNum) * eased;

      displayValues.value[i] = current;
      pulsingCards.value[i] =
        middleNum != null &&
        ((startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum));

      animateValue(i, cp.start, cp.end, cp.middle, {
        startFrom: current,
        durationMs: duration * (1 - cycleProgress),
        loop: true,
      });
    }
    return true;
  } catch (_) {
    return false;
  }
};

const animateValue = (index, start, end, middle, opts = {}) => {
  const startNum = Number(start) || 0;
  const endNum = Number(end) || 0;
  const middleNum = middle != null && middle !== '' ? Number(middle) : null;
  const duration = opts.durationMs ?? getCardDuration(start, end);
  const animStart = opts.startFrom;
  const useResume = animStart != null;
  const loop = opts.loop === true;

  const startTime = performance.now();
  if (!useResume) pulsingCards.value[index] = false;

  const runCycle = (fromVal, dur) => {
    const cycleStart = performance.now();

    const step = (now) => {
      const elapsed = now - cycleStart;
      const progress = Math.min(elapsed / dur, 1);
      const eased = linear(progress);
      const current = fromVal + (endNum - fromVal) * eased;
      displayValues.value[index] = current;

      if (middleNum != null) {
        const reachedMiddle = (startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum);
        if (reachedMiddle) {
          pulsingCards.value[index] = true;
        }
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        displayValues.value[index] = endNum;
        pulsingCards.value[index] = false;
        if (loop) {
          displayValues.value[index] = startNum;
          runCycle(startNum, getCardDuration(start, end));
        }
      }
    };

    requestAnimationFrame(step);
  };

  runCycle(useResume ? animStart : startNum, duration);
};

const runAnimations = () => {
  const resumed = loadAndResume();
  if (resumed) return;

  displayValues.value = [
    Number(props.card1Start) || 0,
    Number(props.card2Start) || 0,
    Number(props.card3Start) || 0,
    Number(props.card4Start) || 0,
  ];
  pulsingCards.value = [false, false, false, false];
  saveAnimationState();

  const cards = cardProps.value;
  cards.forEach((cp, i) => animateValue(i, cp.start, cp.end, cp.middle, { loop: true }));
};

onMounted(runAnimations);

watch(
  () => [props.card1, props.card2, props.card3, props.card4, props.card1Start, props.card2Start, props.card3Start, props.card4Start, props.card1Middle, props.card2Middle, props.card3Middle, props.card4Middle, props.secondsPerUnit],
  runAnimations,
  { deep: true }
);

const jackpots = computed(() =>
  [0, 1, 2, 3].map((i) => ({
    value: displayValues.value[i],
    pulsing: pulsingCards.value[i],
    bgImage: `/jackpot-cards/bell${i + 1}.png`,
  }))
);
</script>

<template>
  <div class="jackpot-bells">
    <div class="jackpot-bells__layout">
      <div class="jackpot-bells__brand">
        <img
          v-show="!mainLogoError"
          src="/jackpot-cards/bell-link-logo.png"
          alt="BELL Link"
          class="jackpot-bells__logo"
          @error="mainLogoError = true"
        />
        <div v-show="mainLogoError" class="jackpot-bells__logo-fallback">
          <span class="jackpot-bells__title">BELL Link</span>
        </div>
      </div>

      <div class="jackpot-bells__bars">
        <div
          v-for="(jp, i) in jackpots"
          :key="i"
          class="jackpot-bells__bar"
          :class="[`jackpot-bells__bar--${i}`, { 'jackpot-bells__bar--pulsing': jp.pulsing }]"
          :style="{ backgroundImage: `url(${jp.bgImage})` }"
        >
        <div class="jackpot-bells__content">
          <div class="jackpot-bells__value-wrap">
            <span class="jackpot-bells__value">{{ formatValue(jp.value) }}</span>
          </div>
          <div class="jackpot-bells__currency-section">
            <div class="jackpot-bells__currency-divider"></div>
            <div class="jackpot-bells__currency-wrap">
              <span
                v-for="letter in (currencyCode || 'TRY').split('')"
                :key="letter"
                class="jackpot-bells__currency-letter"
              >{{ letter }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
.jackpot-bells {
  @apply mx-2 mt-3 mb-2;
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.jackpot-bells__layout {
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 140px;
}

.jackpot-bells__brand {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 70px;
}

.jackpot-bells__logo {
  max-height: 85px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.jackpot-bells__logo-fallback {
  text-align: center;
}

.jackpot-bells__title {
  font-size: 1.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #e8c547 0%, #9c27b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.08em;
}

.jackpot-bells__bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  overflow: visible;
  min-width: 0;
}

.jackpot-bells__bar {
  position: relative;
  display: flex;
  align-items: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  width: 100%;
  min-height: 42px;
  max-height: 48px;
  padding: 0.3rem 3.5rem 0.3rem 0.7rem;
  overflow: visible;
}

.jackpot-bells__bar--0 { max-width: 100%; min-width: 100%; }
.jackpot-bells__bar--1 { max-width: 88%; min-width: 75%; }
.jackpot-bells__bar--2 { max-width: 76%; min-width: 60%; }
.jackpot-bells__bar--3 { max-width: 64%; min-width: 50%; }

.jackpot-bells__bar--pulsing {
  animation: jackpot-bells-pulse 0.8s ease-in-out infinite;
}

@keyframes jackpot-bells-pulse {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.15); }
}

.jackpot-bells__content {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.jackpot-bells__value-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.15rem 0;
}

.jackpot-bells__value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.jackpot-bells__currency-section {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
}

.jackpot-bells__currency-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.4);
  margin: 0 0.35rem;
}

.jackpot-bells__currency-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0.06rem 0.08rem;
}

.jackpot-bells__currency-letter {
  font-size: 0.55rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.1;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

</style>